/* eslint-disable import/prefer-default-export */
import useAxios from '../hooks/useAxios';

/**
 * Get SPC list.
 * @param {Object} params - The parameters object.
 * @param {Object} params.data - The data object containing the following properties:
 * @param {string} params.data.checkBegin - The start date.
 * @param {string} params.data.checkEnd - The end date.
 * @param {string} params.data.scOdno - The SC OD number.
 * @param {string} params.data.customerAlias - The customer alias.
 * @param {string} params.data.prodNameList - The product name.
 * @param {CancelToken} params.cancelToken - The cancel token for request cancellation.
 * @param {function} [params.onSuccess] - The function to call when the request is successful.
 * @param {function} [params.onError] - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function getSPCList(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode, noPrivilegeMsg] = await useAxios(
    '/spcList',
    data,
    true,
    cancelToken
  );
  onSettled?.();
  if (status) {
    onSuccess?.(result);
  } else {
    onError?.(result, rtCode, noPrivilegeMsg);
  }
}

/**
 * Get SPC advanced selections.
 * @param {Object} params - The parameters object.
 * @param {Object} params.data - The data object containing the following properties:
 * @param {string} params.data.checkBegin - The start date.
 * @param {string} params.data.checkEnd - The end date.
 * @param {CancelToken} params.cancelToken - The cancel token for request cancellation.
 * @param {function} [params.onSuccess] - The function to call when the request is successful.
 * @param {function} [params.onError] - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function getSPCAdvancedSelections(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode, noPrivilegeMsg] = await useAxios(
    '/spcTitleList',
    data,
    true,
    cancelToken
  );
  onSettled?.();
  if (status && result.work.length > 0 && result.customer.length > 0) {
    onSuccess?.(result);
  } else if (status && (result.work.length === 0 || result.customer.length === 0)) {
    onError?.(
      result,
      rtCode,
      noPrivilegeMsg,
      '您所選的時間區間沒有相關的進階選項，請嘗試其他時間區間，或請通知網管人員！'
    );
  } else {
    onError?.(
      result,
      rtCode,
      noPrivilegeMsg,
      `${result}：目前暫時無法取得進階選項資訊，請稍後再試，或請通知網管人員！`
    );
  }
}

/**
 * Get SPC line chart.
 * @param {Object} params - The parameters object.
 * @param {Object} params.data - The data object containing the following properties:
 * @param {string} params.data.measureId - The measure ID.
 * @param {string} params.data.checkBegin - The start date.
 * @param {string} params.data.checkEnd - The end date.
 * @param {CancelToken} params.cancelToken - The cancel token for request cancellation.
 * @param {function} [params.onSuccess] - The function to call when the request is successful.
 * @param {function} [params.onError] - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function getSPCLineChart(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode, noPrivilegeMsg] = await useAxios(
    '/spcLineChart',
    data,
    true,
    cancelToken
  );
  onSettled?.();
  if (status) {
    onSuccess?.(result);
  } else {
    onError?.(result, rtCode, noPrivilegeMsg);
  }
}
